import { CampaignCandidateDetail } from "@components/campaigns/CampaignCandidateDetail";
import { CampaignDetail } from "@components/campaigns/CampaignDetail";
import { CampaignList } from "@components/campaigns/CampaignList";
import { CampaignSettings } from "@components/campaigns/CampaignSettings";
import { CandidateScoringMatrixPage } from "@components/campaigns/CandidateScoringMatrixPage";
import { ActivityTab } from "@components/campaigns/detail/ActivityTab";
import { DefineSection } from "@components/campaigns/detail/DefineSection";
import {
  AssessmentDefineSlot,
  BlueprintDefineSlot,
  BriefDefineSlot,
  MarketMapDefineSlot,
  SelectionProcessDefineSlot,
} from "@components/campaigns/detail/defineSlots";
import { LineageTab } from "@components/campaigns/detail/LineageTab";
import { MigrationStub } from "@components/campaigns/detail/MigrationStub";
import { OverviewTab } from "@components/campaigns/detail/OverviewTab";
import { PipelineSection } from "@components/campaigns/detail/PipelineSection";
import { RunSection } from "@components/campaigns/detail/RunSection";
import {
  DiscoveryRunSlot,
  InterviewsRunSlot,
  LonglistRunSlot,
  OffersRunSlot,
  OutreachRunSlot,
  PipelineOverviewRunSlot,
  PoolRunSlot,
  ScoringRunSlot,
  ShortlistRunSlot,
} from "@components/campaigns/detail/runSlots";
import {
  DEFINE_SLOTS,
  defaultSlotPath,
  PIPELINE_SLOTS,
  RUN_SLOTS,
} from "@components/campaigns/detail/sections";
import { TeamTab } from "@components/campaigns/detail/TeamTab";
import { LonglistExecutiveSummaryPage } from "@components/campaigns/LonglistExecutiveSummaryPage";
import { BrandedJobPostingPage } from "@components/campaigns/outreach/BrandedJobPostingPage";
import { LinkedinJobPostingPage } from "@components/campaigns/outreach/LinkedinJobPostingPage";
import { CandidateDetail } from "@components/candidates/CandidateDetail";
import { CandidateList } from "@components/candidates/CandidateList";
import { ChangelogPopup } from "@components/changelog/ChangelogPopup";
import { ThreadDebug } from "@components/chat/ThreadDebug";
import { ClientDetail } from "@components/clients/ClientDetail";
import { ClientList } from "@components/clients/ClientList";
import { Dashboard } from "@components/dashboard/Dashboard";
import { GuideIndex } from "@components/help/GuideIndex";
import { GuideRoute } from "@components/help/GuideRoute";
import { AppShell } from "@components/layout/AppShell";
import { OutreachPage } from "@components/outreach/OutreachPage";
import { OrganisationSettings } from "@components/settings/OrganisationSettings";
import { TalentPoolDetail } from "@components/talent-pools/TalentPoolDetail";
import { TalentPoolList } from "@components/talent-pools/TalentPoolList";
import { TermsAcceptanceGate } from "@components/terms/TermsAcceptanceGate";
import type { Organisation, TermsVersion, User } from "@lib/types";
import { type ReactElement, useState } from "react";
import { Navigate, Route, Routes, useParams, useSearchParams } from "react-router";

export interface Impersonation {
  impersonatedUserName: string;
  trueUserName: string;
  stopPath: string;
  csrfToken: string;
}

interface AppProps {
  currentUser: User | null;
  currentOrganisation: Organisation | null;
  pendingTerms: TermsVersion[];
  impersonation: Impersonation | null;
}

function OverviewRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={`overview${qs ? `?${qs}` : ""}`} replace />;
}

// Legacy /campaigns/:id/pipeline path now lands on the RUN pipeline surface.
// (The stepper it used to point at was retired in #2219.)
export function LegacyPipelineRedirect() {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={`../run/pipeline${qs ? `?${qs}` : ""}`} replace />;
}

// The linear stepper lived at /campaigns/:id/workflow?stage=N until #2219. Map
// old bookmarks onto the slot that absorbed each stage; anything unrecognised
// lands on the RUN pipeline. Paths are relative to /campaigns/:id.
const WORKFLOW_STAGE_PATHS: Record<string, string> = {
  "1": "define/brief",
  "2": "define/blueprint",
  "3": "define/market-map",
  "4": "run/pipeline/longlist",
  "5": "run/outreach",
  "6": "run/pipeline/scoring",
  "7": "run/pipeline/shortlist",
  "8": "run/interviews",
  "9": "run/offers",
  "10": "run/offers",
};

export function LegacyWorkflowRedirect() {
  const [params] = useSearchParams();
  const target = WORKFLOW_STAGE_PATHS[params.get("stage") ?? ""] ?? "run/pipeline";
  return <Navigate to={`../${target}`} replace />;
}

// The per-kind outreach draft pages folded into the RUN › Outreach workspace
// (#3169). Keep their bookmarks and older chat deep links working.
function LegacyOutreachRedirect({ channel }: { channel: string }) {
  const [params] = useSearchParams();
  const cc = params.get("cc");
  const query = `?channel=${channel}${cc ? `&cc=${encodeURIComponent(cc)}` : ""}`;
  return <Navigate to={`../run/outreach${query}`} replace />;
}

// The standalone assessment matrix is now embedded under DEFINE › Assessment
// (#2220). Keep the old /assessment-matrix bookmark working via a redirect.
export function AssessmentMatrixRedirect() {
  const { id } = useParams();
  return <Navigate to={`/campaigns/${id}/define/assessment`} replace />;
}

// DEFINE slots whose editors have been migrated off the stepper (#2216). Slots
// absent here still render a MigrationStub until their story lands.
const DEFINE_SLOT_ELEMENTS: Partial<Record<string, ReactElement>> = {
  brief: <BriefDefineSlot />,
  blueprint: <BlueprintDefineSlot />,
  "selection-process": <SelectionProcessDefineSlot />,
  "market-map": <MarketMapDefineSlot />,
  assessment: <AssessmentDefineSlot />,
};

// RUN leaf slots (#2218). Pipeline is a nested parent route handled separately.
const RUN_SLOT_ELEMENTS: Partial<Record<string, ReactElement>> = {
  discovery: <DiscoveryRunSlot />,
  pool: <PoolRunSlot />,
  outreach: <OutreachRunSlot />,
  interviews: <InterviewsRunSlot />,
  offers: <OffersRunSlot />,
};

const PIPELINE_SLOT_ELEMENTS: Partial<Record<string, ReactElement>> = {
  overview: <PipelineOverviewRunSlot />,
  longlist: <LonglistRunSlot />,
  scoring: <ScoringRunSlot />,
  shortlist: <ShortlistRunSlot />,
};

export function App({ currentUser, currentOrganisation, pendingTerms, impersonation }: AppProps) {
  const [pendingQueue, setPendingQueue] = useState<TermsVersion[]>(pendingTerms);

  if (!currentUser) {
    return null;
  }

  const nextPending = pendingQueue[0];

  return (
    <AppShell
      currentUser={currentUser}
      currentOrganisation={currentOrganisation}
      impersonation={impersonation}
    >
      {nextPending ? (
        <TermsAcceptanceGate
          termsVersion={nextPending}
          onAccepted={() => setPendingQueue((queue) => queue.slice(1))}
        />
      ) : (
        <ChangelogPopup />
      )}
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clients" element={<ClientList />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/campaigns" element={<CampaignList />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />}>
          <Route index element={<OverviewRedirect />} />
          <Route path="define" element={<DefineSection />}>
            <Route index element={<Navigate to={defaultSlotPath(DEFINE_SLOTS)} replace />} />
            {DEFINE_SLOTS.map((slot) => (
              <Route
                key={slot.to}
                path={slot.to}
                element={DEFINE_SLOT_ELEMENTS[slot.to] ?? <MigrationStub slot={slot} />}
              />
            ))}
          </Route>
          <Route path="run" element={<RunSection />}>
            <Route index element={<Navigate to={defaultSlotPath(RUN_SLOTS)} replace />} />
            <Route path="pipeline" element={<PipelineSection />}>
              <Route index element={<Navigate to={defaultSlotPath(PIPELINE_SLOTS)} replace />} />
              {PIPELINE_SLOTS.map((slot) => (
                <Route
                  key={slot.to}
                  path={slot.to}
                  element={PIPELINE_SLOT_ELEMENTS[slot.to] ?? <MigrationStub slot={slot} />}
                />
              ))}
            </Route>
            {RUN_SLOTS.filter((slot) => slot.to !== "pipeline").map((slot) => (
              <Route
                key={slot.to}
                path={slot.to}
                element={RUN_SLOT_ELEMENTS[slot.to] ?? <MigrationStub slot={slot} />}
              />
            ))}
          </Route>
          <Route path="overview" element={<OverviewTab />} />
          <Route path="workflow" element={<LegacyWorkflowRedirect />} />
          <Route path="pool" element={<Navigate to="../run/pool" replace />} />
          <Route path="pipeline" element={<LegacyPipelineRedirect />} />
          <Route path="outreach" element={<Navigate to="../run/outreach" replace />} />
          <Route
            path="outreach/email-messages"
            element={<LegacyOutreachRedirect channel="email" />}
          />
          <Route
            path="outreach/linkedin-messages"
            element={<LegacyOutreachRedirect channel="linkedin_message" />}
          />
          <Route
            path="outreach/linkedin-connection-requests"
            element={<LegacyOutreachRedirect channel="linkedin_connection_request" />}
          />
          <Route path="activity" element={<ActivityTab />} />
          <Route path="lineage" element={<LineageTab />} />
          <Route path="team" element={<TeamTab />} />
          <Route path="settings" element={<CampaignSettings />} />
        </Route>
        <Route path="/campaigns/:id/linkedin-job-posting" element={<LinkedinJobPostingPage />} />
        <Route path="/campaigns/:id/branded-job-posting" element={<BrandedJobPostingPage />} />
        <Route
          path="/campaigns/:id/longlist-executive-summary"
          element={<LonglistExecutiveSummaryPage />}
        />
        <Route
          path="/campaigns/:id/candidate-scoring-matrix"
          element={<CandidateScoringMatrixPage />}
        />
        <Route path="/campaigns/:id/assessment-matrix" element={<AssessmentMatrixRedirect />} />
        <Route
          path="/campaigns/:campaignId/candidates/:campaignCandidateId"
          element={<CampaignCandidateDetail />}
        />
        <Route path="/candidates" element={<CandidateList />} />
        <Route path="/candidates/:id" element={<CandidateDetail />} />
        <Route path="/talent-pools" element={<TalentPoolList />} />
        <Route path="/talent-pools/:id" element={<TalentPoolDetail />} />
        <Route path="/outreach" element={<OutreachPage />} />
        <Route path="/settings" element={<OrganisationSettings />} />
        <Route path="/chat/threads/:id/debug" element={<ThreadDebug />} />
        <Route path="/help" element={<GuideIndex />} />
        <Route path="/help/:slug" element={<GuideRoute />} />
      </Routes>
    </AppShell>
  );
}
